import React from "react";
import { ItemUI, emptyItem } from "../types/item";
import { User, createNewUser } from "../types/user";
import { useReceipt } from "../data/ReceiptContext";
import {
  getReceipt,
  createReceipt,
  fromPersisted,
  subscribeReceipt,
  setItemShare,
  setItemSplits,
  setItemsSplits,
  claimAllOpen,
  unclaimAll,
  deleteItems,
  addRosterUser,
  saveReceiptContent,
  saveUserVenmo,
} from "../data/Receipt";
import { rememberPerson } from "../data/knownPeople";
import { getLocalIdentity, setLocalIdentity } from "../data/localIdentity";
import { getMyName, getMyVenmo, setMyVenmo } from "../data/myProfile";

// coalesce rapid split/share taps into one write (fewer contending transactions)
const WRITE_DEBOUNCE_MS = 350;

/**
 * Owns all the Firestore-coupled receipt state for the Main page: loading a
 * shared receipt, the live subscription (guest content + item assignments),
 * persisting host edits, and the identity/assignment/roster handlers. Keeps a single `subscribeReceipt` listener so there are no extra
 * Firestore reads.
 */
export function useReceiptSync(id: string | undefined, uid: string | null) {
  const { updateReceipt, setPictureUploaded, receipt } = useReceipt();

  // the receipt's creator (anon uid), known once loaded/created
  const [ownerId, setOwnerId] = React.useState<string | null>(null);

  const [title, setTitle] = React.useState<string>("CheckMates");
  const [items, setItems] = React.useState<ItemUI[]>([emptyItem]);
  const [users, setUsers] = React.useState<User[]>(() => [
    // "Me" starts with the name + venmo you last saved on this device
    createNewUser(getMyName() || "Me", getMyVenmo() || undefined),
  ]);
  const [selectedUserIndex, setSelectedUserIndex] = React.useState<
    number | null
  >(0);
  const [whichUserPaidIndex, setWhichUserPaidIndex] = React.useState<number>(0);
  const [tip, setTip] = React.useState<number>(0);
  const [tax, setTax] = React.useState<number>(0);
  const [loadingReceipt, setLoadingReceipt] = React.useState<boolean>(!!id);
  // which mate this device says you are (guests). Device-only — never sent to
  // the receipt, so several devices can be the same mate.
  const [me, setMeState] = React.useState<string | null>(() =>
    id ? getLocalIdentity(id) : null
  );
  React.useEffect(() => {
    setMeState(id ? getLocalIdentity(id) : null);
  }, [id]);

  // number of share/splits writes in flight; while > 0 we trust optimistic UI
  const pendingAssignmentsRef = React.useRef(0);
  // while a guest is adding themselves to the roster, don't let a snapshot drop them
  const pendingRosterRef = React.useRef(false);
  // gates host content persistence: only after a shared receipt has loaded, and
  // skip the write the load itself triggers
  const contentLoadedRef = React.useRef(false);
  const skipContentPersistRef = React.useRef(false);
  // while a guest is editing their own venmo, don't let a snapshot revert it
  const pendingVenmoRef = React.useRef(false);
  const venmoTimerRef = React.useRef<ReturnType<typeof setTimeout>>();
  // the id of a receipt we already hold in state (just created) — skip re-loading it
  const loadedIdRef = React.useRef<string | null>(null);
  // debounced split/share writes, keyed so rapid taps coalesce into one write
  const writeTimersRef = React.useRef<
    Map<string, { timer: ReturnType<typeof setTimeout>; run: () => Promise<void> }>
  >(new Map());
  // the identity we've already positioned the selection on, so viewing another
  // user's split isn't yanked back on every snapshot
  const autoSelectedMeRef = React.useRef<string | null>(null);

  // owner (or a brand-new receipt) can edit non-item content; a loaded receipt
  // we don't own is a guest. Item names/prices lock once shared, but anyone —
  // owner or guest — can change each item's split count (denominator).
  const canEdit = !id || ownerId === uid;
  const canEditItems = !id;
  const canEditSplits = true;

  // load a shared receipt when the URL has an id
  React.useEffect(() => {
    if (!id) return;
    // we just created this receipt and already have its data — don't reload
    if (loadedIdRef.current === id) {
      setLoadingReceipt(false);
      return;
    }
    let active = true;
    setLoadingReceipt(true);
    getReceipt(id)
      .then((r) => {
        if (!active || !r) {
          if (active) setLoadingReceipt(false);
          return;
        }
        setOwnerId(r.ownerId);
        const draft = fromPersisted(r);
        setTitle(draft.title);
        setItems(draft.items);
        setUsers(draft.users);
        setTax(draft.tax);
        setTip(draft.tip);
        const paidIdx = draft.paidUserId
          ? draft.users.findIndex((u) => u.id === draft.paidUserId)
          : 0;
        setWhichUserPaidIndex(paidIdx === -1 ? 0 : paidIdx);
        setSelectedUserIndex(draft.users.length > 0 ? 0 : null);
        if (r.pictureUrl) {
          updateReceipt({ pictureUrl: r.pictureUrl });
          setPictureUploaded(true);
        }
        // content is now in sync with the backend; don't persist the load itself
        contentLoadedRef.current = true;
        skipContentPersistRef.current = true;
        setLoadingReceipt(false);
      })
      .catch(() => active && setLoadingReceipt(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // live sync for a shared receipt: content + item assignments. Only the
  // assignments (each item's users) are merged in, so local content edits
  // (names/prices) aren't clobbered.
  React.useEffect(() => {
    if (!id) return;
    return subscribeReceipt(id, (r) => {
      if (!r) return;
      setOwnerId(r.ownerId);

      // guests receive the host's live content edits (roster/venmo, tax, tip)
      if (!canEdit) {
        setTitle(r.title);
        setTax(r.tax);
        setTip(r.tip);
        // don't clobber a venmo the guest is typing, or a roster add in flight
        if (!pendingVenmoRef.current && !pendingRosterRef.current)
          setUsers(r.users);
        const paidIdx = r.paidUserId
          ? r.users.findIndex((u) => u.id === r.paidUserId)
          : 0;
        setWhichUserPaidIndex(paidIdx === -1 ? 0 : paidIdx);
      }

      // don't let an in-flight write's snapshot revert an optimistic edit; once
      // all share/splits writes settle, the next snapshot reconciles items
      if (pendingAssignmentsRef.current > 0) return;

      // merge each item's live splits + shares from this snapshot, keeping local
      // name/price (locked once shared, so they already match), and dropping any
      // item removed on the server so bulk deletes propagate to everyone
      const serverById = new Map(r.items.map((it) => [it.id, it]));
      setItems((prev) =>
        prev.flatMap((it) => {
          const server = serverById.get(it.id);
          if (!server) return [];
          return [
            {
              ...it,
              splits: server.splits ?? it.splits,
              shares: server.shares ?? {},
            },
          ];
        })
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, uid, canEdit]);

  // in guest mode, position the selection on "me" once (when it's set /
  // appears in the roster). We don't re-yank afterwards, so you can select
  // someone else to view their split.
  React.useEffect(() => {
    if (canEdit || !me) return;
    if (autoSelectedMeRef.current === me) return;
    const idx = users.findIndex((u) => u.id === me);
    if (idx !== -1) {
      setSelectedUserIndex(idx);
      autoSelectedMeRef.current = me;
    }
  }, [me, users, canEdit]);

  // persist host content edits (roster/venmo, tax, tip, title, payer) so guests
  // see them live. Skips pre-load and the load-triggered change; debounced so
  // rapid edits (e.g. typing the title) coalesce into one write.
  React.useEffect(() => {
    if (!id || !canEdit || !contentLoadedRef.current) return;
    if (skipContentPersistRef.current) {
      skipContentPersistRef.current = false;
      return;
    }
    const timer = setTimeout(() => {
      saveReceiptContent(id, {
        title,
        tax,
        tip,
        users,
        paidUserId: users[whichUserPaidIndex]?.id ?? null,
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [id, canEdit, title, tax, tip, users, whichUserPaidIndex]);

  // set (or clear) which mate this device is — local only, instant
  const setMe = (userId: string | null) => {
    if (!id) return;
    setMeState(userId);
    setLocalIdentity(id, userId);
  };

  // set a roster user's venmo (a guest editing their own handle, or the
  // autofill below): optimistic + remembered locally + debounced write to the
  // shared receipt.
  const handleUserVenmoChange = (userId: string, venmo: string) => {
    setUsers((prev) =>
      prev.map((u) => (u.id === userId ? { ...u, venmo: venmo || undefined } : u))
    );
    setMyVenmo(venmo); // remember for autofill on future receipts
    if (!id) return;
    pendingVenmoRef.current = true;
    clearTimeout(venmoTimerRef.current);
    venmoTimerRef.current = setTimeout(() => {
      saveUserVenmo(id, userId, venmo).finally(() => {
        pendingVenmoRef.current = false;
      });
    }, 500);
  };

  // guest autofill: on becoming a mate with no venmo, seed it from
  // localStorage. Only the mate you are — never one you're just viewing.
  React.useEffect(() => {
    // never the first mate ("Me", the payer) — that's the owner's venmo
    if (canEdit || selectedUserIndex === null || selectedUserIndex === 0)
      return;
    const user = users[selectedUserIndex];
    if (!user || user.id !== me) return;
    const saved = getMyVenmo();
    if (!user.venmo && saved) handleUserVenmoChange(user.id, saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserIndex, users, canEdit, me]);

  // Debounced, keyed persistence: the latest value per key wins, and only one
  // transaction fires per burst (rapid taps no longer contend and drop writes).
  // pendingAssignmentsRef stays > 0 for the whole window so the subscription
  // doesn't revert the optimistic value before the write lands.
  const scheduleWrite = (key: string, run: () => Promise<void>) => {
    const timers = writeTimersRef.current;
    const existing = timers.get(key);
    if (existing) clearTimeout(existing.timer);
    else pendingAssignmentsRef.current += 1;
    const timer = setTimeout(() => {
      timers.delete(key);
      run().finally(() => {
        pendingAssignmentsRef.current = Math.max(
          0,
          pendingAssignmentsRef.current - 1
        );
      });
    }, WRITE_DEBOUNCE_MS);
    timers.set(key, { timer, run });
  };

  // flush any pending debounced writes on unmount so a last edit isn't lost
  React.useEffect(() => {
    const timers = writeTimersRef.current;
    return () => {
      timers.forEach(({ timer, run }) => {
        clearTimeout(timer);
        run();
      });
      timers.clear();
    };
  }, []);

  // on a shared receipt, share/splits edits persist so everyone sees them live.
  // ItemList has already updated the UI optimistically; we just debounce-write.
  const handleSetShare = (itemId: string, userId: string, shares: number) => {
    if (!id) return;
    scheduleWrite(`share:${itemId}:${userId}`, () =>
      setItemShare(id, itemId, userId, shares)
    );
  };

  const handleSetSplits = (itemId: string, splits: number) => {
    if (!id) return;
    scheduleWrite(`splits:${itemId}`, () => setItemSplits(id, itemId, splits));
  };

  // bulk-set the split count on many items at once (from the bulk editor).
  // Optimistic locally (capping shares to the new denominator), one debounced
  // transaction to persist them together.
  const handleBulkSetSplits = (itemIds: string[], splits: number) => {
    const s = Math.max(1, splits);
    const targets = new Set(itemIds);
    setItems((prev) =>
      prev.map((item) => {
        if (!targets.has(item.id)) return item;
        const next: Record<string, number> = {};
        for (const [u, n] of Object.entries(item.shares)) {
          const capped = Math.min(n, s);
          if (capped > 0) next[u] = capped;
        }
        return { ...item, splits: s, shares: next };
      })
    );
    if (id) scheduleWrite("bulk-splits", () => setItemsSplits(id, itemIds, s));
  };

  // "Claim all": give a user every open portion on the check. Optimistic
  // locally, one debounced transaction to persist (capped server-side against
  // anyone else's concurrent claims).
  const handleClaimAll = (userId: string) => {
    setItems((prev) =>
      prev.map((item) => {
        const others = Object.entries(item.shares)
          .filter(([u]) => u !== userId)
          .reduce((sum, [, n]) => sum + n, 0);
        const available = item.splits - others;
        if (available <= (item.shares[userId] ?? 0)) return item;
        return { ...item, shares: { ...item.shares, [userId]: available } };
      })
    );
    if (id) scheduleWrite(`all:${userId}`, () => claimAllOpen(id, userId));
  };

  // "Unclaim all": drop a user's portions from every item (back to open).
  // Shares the "Claim all" write key, so flipping quickly between the two
  // only persists the last one.
  const handleUnclaimAll = (userId: string) => {
    setItems((prev) =>
      prev.map((item) => {
        if (!(userId in item.shares)) return item;
        const { [userId]: _mine, ...rest } = item.shares;
        return { ...item, shares: rest };
      })
    );
    if (id) scheduleWrite(`all:${userId}`, () => unclaimAll(id, userId));
  };

  // bulk-delete items: optimistic local removal + persist (and propagate to
  // guests) when on a shared receipt
  const handleBulkDelete = (itemIds: string[]) => {
    const targets = new Set(itemIds);
    setItems((prev) => prev.filter((item) => !targets.has(item.id)));
    if (id) scheduleWrite("bulk-delete", () => deleteItems(id, itemIds));
  };

  // a guest introducing themselves: add a new roster user, become it, and
  // remember them for future receipts.
  const handleAddGuestIdentity = (name: string, venmo: string) => {
    if (!id || !uid) return;
    const user = createNewUser(name.trim(), venmo.trim() || undefined);
    setUsers((prev) => [...prev, user]); // optimistic
    pendingRosterRef.current = true;
    addRosterUser(id, user).finally(() => {
      pendingRosterRef.current = false;
    });
    setMe(user.id);
    rememberPerson(uid, user);
  };

  // publish the current (local) receipt to Firestore and return its new id.
  // We keep the local data (loadedIdRef) so navigating to /:id won't reload it.
  const share = async (): Promise<string | null> => {
    if (!uid) return null;
    // drop rows the mates table left without a name ("Me" always stays)
    const namedUsers = users.filter((u, i) => i === 0 || u.name.trim() !== "");
    const paidId = users[whichUserPaidIndex]?.id;
    const paidIndex = Math.max(
      0,
      namedUsers.findIndex((u) => u.id === paidId)
    );
    if (namedUsers.length !== users.length) {
      setUsers(namedUsers);
      setWhichUserPaidIndex(paidIndex);
      setSelectedUserIndex(null);
    }
    const newId = await createReceipt(uid, {
      title,
      tax,
      tip,
      pictureUrl: receipt.pictureUrl,
      users: namedUsers,
      items,
      paidUserId: namedUsers[paidIndex]?.id ?? null,
    });
    loadedIdRef.current = newId;
    setOwnerId(uid);
    contentLoadedRef.current = true;
    skipContentPersistRef.current = true;
    return newId;
  };

  const selectedUser: User | null =
    selectedUserIndex !== null ? users[selectedUserIndex] : null;

  // any selected mate's portions can be edited — there's no per-mate locking,
  // so several devices can work on the same mate at once
  const canEditSelectedShares = !!selectedUser;

  return {
    ownerId,
    canEdit,
    canEditItems,
    canEditSplits,
    canEditSelectedShares,
    share,
    title,
    setTitle,
    items,
    setItems,
    users,
    setUsers,
    selectedUserIndex,
    setSelectedUserIndex,
    whichUserPaidIndex,
    setWhichUserPaidIndex,
    tip,
    setTip,
    tax,
    setTax,
    loadingReceipt,
    me,
    setMe,
    selectedUser,
    handleSetShare,
    handleSetSplits,
    handleBulkSetSplits,
    handleClaimAll,
    handleUnclaimAll,
    handleBulkDelete,
    handleAddGuestIdentity,
    handleUserVenmoChange,
  };
}
